import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { useDeferredWork } from "@/hooks/useDeferredWork";
import { usePublicRestaurantCities } from "@/hooks/usePublicRestaurantCities";
import { useQuery } from "@tanstack/react-query";
import { DeferredNewsletterDialog } from "@/components/DeferredNewsletterDialog";
import { useState, useEffect, useMemo, useRef, Suspense } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Restaurant } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, LogOut, Settings, LogIn, User, ArrowUp, Download } from "lucide-react";
import { CartButton } from "@/components/cart/CartButton";
import facebookIcon from "@/assets/facebook-icon.png";
import { MobileTooltip } from "@/components/ui/mobile-tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { cityNameToSlug, slugToCityName } from "@/lib/citySlug";
import { WelcomeDialog } from "@/components/WelcomeDialog";
const PopupDisplay = lazyWithRetry(() => import("@/components/PopupDisplay").then(m => ({ default: m.PopupDisplay })));
const UserProfileDialog = lazyWithRetry(() => import("@/components/UserProfileDialog").then(m => ({ default: m.UserProfileDialog })));

import { RestaurantCard } from "@/components/restaurant/RestaurantCard";
import { RestaurantMap } from "@/components/restaurant/RestaurantMap";
import { BannerAd } from "@/components/restaurant/BannerAd";
const Footer = lazyWithRetry(() => import("@/components/Footer").then(m => ({ default: m.Footer })));
import { LoadingSpinner } from "@/components/LoadingSpinner";
const FavoriteFoodAlert = lazyWithRetry(() => import("@/components/FavoriteFoodAlert").then(m => ({ default: m.FavoriteFoodAlert })));
const OnboardingTour = lazyWithRetry(() => import("@/components/OnboardingTour").then(m => ({ default: m.OnboardingTour })));
import { FloatingContactButton } from "@/components/FloatingContactButton";
import { FloatingRecommendBar } from "@/components/FloatingRecommendBar";
import { SEO } from "@/components/SEO";
const logo = "/napihami_logo.png";
import { APP_VERSION } from "@/lib/version";
import citySilhouette from "@/assets/city_silhouette.png";


import { format } from "date-fns";
import { hu } from "date-fns/locale";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/useAuth";
import { useAllDailyMenus } from "@/hooks/useAllDailyMenus";
import { useBanners } from "@/hooks/useBanners";
import { useRestaurants } from "@/hooks/useRestaurants";
import { useRestaurantPreview, getStoredPreviewToken } from "@/hooks/useRestaurantPreview";
import { PreviewBar } from "@/components/restaurant/PreviewBar";
import { useRestaurantVouchers } from "@/hooks/useRestaurantVouchers";
import { useDistanceLocation } from "@/hooks/useDistanceLocation";
import { sortRestaurantsByDistance } from "@/lib/restaurantDistance";
type SortOption = "random" | "name" | "price" | "distance";
type ViewMode = "1" | "2" | "3";
type FontSizeOption = "0" | "1" | "2" | "3" | "4";
const Index = () => {
  const navigate = useNavigate();
  const { citySlug } = useParams<{ citySlug?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<SortOption>("random");
  const { coordinates, loading: locationLoading, error: locationError, requestLocation, cancelLocation } = useDistanceLocation();
  const [selectedCity, setSelectedCity] = useState<string>("");
  const [userName, setUserName] = useState<string>("");
  const [jumpToRestaurant, setJumpToRestaurant] = useState<string>("");
  const {
    user: authUser,
    session,
    role,
    loading: authLoading
  } = useAuth();
  
  const { data: publicCities = [], isLoading: citiesLoading } = usePublicRestaurantCities();
  const routeCity = citySlug ? slugToCityName(citySlug, publicCities) : null;
  const effectiveCity = routeCity ?? selectedCity;

  // Cache-elt restaurant lekérés - csak aktív éttermek a frontend-en
  const { data: restaurants = [], isLoading: restaurantsLoading } = useRestaurants(
    authUser?.id, 
    role, 
    !authLoading && (!citySlug || Boolean(routeCity)),
    true, // onlyActive: csak aktív éttermek jelenjenek meg
    routeCity ?? undefined
  );
  const [welcomeDialogOpen, setWelcomeDialogOpen] = useState(false);
  const [userProfileDialogOpen, setUserProfileDialogOpen] = useState(false);
  const [profileMounted, setProfileMounted] = useState(false);
  useEffect(() => { if (userProfileDialogOpen) setProfileMounted(true); }, [userProfileDialogOpen]);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("2");
  const [fontSizeOffset, setFontSizeOffset] = useState<FontSizeOption>("0");
  const [isInstalled, setIsInstalled] = useState(false);
  const firstRestaurantRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const [runTour, setRunTour] = useState(false);
  // Observe the global tracking fetch instead of making a second private-table request.
  const { data: trackingSettings } = useQuery({
    queryKey: ["public-tracking-settings"],
    queryFn: async () => {
      const { data, error } = await supabase.from("tracking_codes_public")
        .select("head_scripts, body_scripts, facebook_url").limit(1).maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: false,
  });
  const facebookUrl = trackingSettings?.facebook_url ?? "";
  // Magyar idő szerinti "logikai nap": 19:00 után már a következő napot mutatjuk
  const getLogicalDate = () => {
    const now = new Date();
    // Budapest időzóna: óra lekérdezése
    const budapestHour = parseInt(
      now.toLocaleString("en-US", { timeZone: "Europe/Budapest", hour: "numeric", hour12: false })
    );
    const date = new Date(
      now.toLocaleString("en-US", { timeZone: "Europe/Budapest" })
    );
    if (budapestHour >= 19) {
      date.setDate(date.getDate() + 1);
    }
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const safeFacebookUrl = useMemo(() => {
    let url = facebookUrl?.trim();
    if (!url) return "";
    if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
    return url;
  }, [facebookUrl]);

  const [selectedDate, setSelectedDate] = useState(getLogicalDate);

  // Date navigation logic
  const today = useMemo(() => {
    return getLogicalDate();
  }, []);
  const endOfWeek = useMemo(() => {
    const date = new Date(today);
    const dayOfWeek = today.getDay();
    if (dayOfWeek === 0) {
      // Ha ma vasárnap van, az a hét vége
      return date;
    }
    // Egyébként számold ki a vasárnapot
    date.setDate(today.getDate() + (7 - dayOfWeek));
    return date;
  }, [today]);
  const isToday = selectedDate.toDateString() === today.toDateString();
  const canGoPrevious = selectedDate > today;
  const canGoNext = selectedDate < endOfWeek;
  const goToPreviousDay = () => {
    if (canGoPrevious) {
      const newDate = new Date(selectedDate);
      newDate.setDate(newDate.getDate() - 1);
      newDate.setHours(0, 0, 0, 0);
      setSelectedDate(newDate);
    }
  };
  const goToNextDay = () => {
    if (canGoNext) {
      const newDate = new Date(selectedDate);
      newDate.setDate(newDate.getDate() + 1);
      newDate.setHours(0, 0, 0, 0);
      setSelectedDate(newDate);
    }
  };

  // Handle social signup callback
  useEffect(() => {
    const handleSocialSignup = async () => {
      const isSocialSignup = searchParams.get("social_signup") === "true";
      const cityFromUrl = searchParams.get("city");
      if (isSocialSignup && authUser) {
        try {
          // Check if user already has a subscription
          const {
            data: existingSub
          } = await supabase.from("newsletter_subscriptions").select("id").eq("user_id", authUser.id).single();
          if (!existingSub) {
            // Get city ID
            const cityName = cityFromUrl || localStorage.getItem("napihami_city") || "";
            let cityId: string | null = null;
            if (cityName) {
              const {
                data: cityData
              } = await supabase.from("cities").select("id").eq("name", cityName).single();
              cityId = cityData?.id || null;
            }

            // Get user metadata from OAuth
            const firstName = authUser.user_metadata?.first_name || authUser.user_metadata?.name?.split(" ")[0] || "";
            const lastName = authUser.user_metadata?.last_name || authUser.user_metadata?.name?.split(" ").slice(1).join(" ") || "";

            // Update or create profile
            await supabase.from("profiles").upsert({
              id: authUser.id,
              email: authUser.email,
              first_name: firstName,
              last_name: lastName
            });

            // Ensure guest role server-side (RLS blocks direct client writes)
            const {
              error: roleError
            } = await supabase.rpc("ensure_guest_role");
            if (roleError) {
              console.error("Error ensuring guest role:", roleError);
            }
            if (cityId) {
              // Create subscription (active because email is already verified via OAuth)
              const {
                data: subscriptionData
              } = await supabase.from("newsletter_subscriptions").insert({
                user_id: authUser.id,
                city_id: cityId,
                is_active: true,
                status: "confirmed"
              }).select("id").single();
              toast.success("Sikeres feliratkozás a napi ebédértesítőre!");
            } else {
              toast.info("Válassz várost a profil beállításaiban a feliratkozás befejezéséhez!");
              setUserProfileDialogOpen(true);
            }
          }

          // Clear URL params
          setSearchParams({});
        } catch (error) {
          console.error("Social signup error:", error);
        }
      }
    };
    if (authUser && !restaurantsLoading) {
      handleSocialSignup();
    }
  }, [authUser, searchParams, restaurantsLoading]);
  const cities = useMemo(() => Array.from(new Set([...publicCities, ...restaurants.flatMap(r => r.city ? [r.city] : [])])).sort(), [publicCities, restaurants]);

  // Resolve city from URL slug or localStorage
  useEffect(() => {
    if (cities.length === 0) return; // Wait for cities to be available

    if (citySlug) {
      // URL has a city slug - resolve it
      const cityName = slugToCityName(citySlug, cities);
      if (cityName) {
        setSelectedCity(cityName);
        localStorage.setItem("napihami_city", cityName);
      } else {
        // Invalid slug - redirect to 404
        navigate("/404", { replace: true });
        return;
      }
    } else {
      // No slug in URL - check saved city and redirect
      const savedCity = localStorage.getItem("napihami_city");
      if (savedCity && cities.includes(savedCity)) {
        navigate(`/${cityNameToSlug(savedCity)}`, { replace: true });
        return;
      }
    }
  }, [citySlug, cities, navigate]);

  useEffect(() => {

    const savedName = localStorage.getItem("napihami_username");
    const savedFavorites = localStorage.getItem("napihami_favorites");
    const savedSortBy = document.cookie.split('; ').find(cookie => cookie.startsWith('napihami_sortby='))?.split('=')[1]
      || localStorage.getItem("napihami_sortby");
    const savedViewMode = localStorage.getItem("napihami_viewmode");
    const savedFontSize = localStorage.getItem("napihami_fontsize");
    if (savedName && !authUser) setUserName(savedName);
    if (savedSortBy && ["random", "name", "price", "distance"].includes(savedSortBy)) setSortBy(savedSortBy as SortOption);
    if (savedViewMode) setViewMode(savedViewMode as ViewMode);
    if (savedFontSize) setFontSizeOffset(savedFontSize as FontSizeOption);
    if (savedFavorites) {
      try {
        setFavorites(JSON.parse(savedFavorites));
      } catch (e) {
        console.error("Error parsing favorites:", e);
      }
    }

    // Check if user has visited before - if not, show welcome dialog
    const hasVisited = localStorage.getItem("napihami_welcomed");
    const savedCity = localStorage.getItem("napihami_city");
    if (!hasVisited || !savedCity || !savedName) {
      setWelcomeDialogOpen(true);
    }
  }, [authUser]);

  // Fetch profile first_name for logged-in users
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (authUser) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("first_name")
          .eq("id", authUser.id)
          .single();
        
        if (profile?.first_name) {
          setUserName(profile.first_name);
        }
      }
    };
    
    fetchUserProfile();
  }, [authUser]);

  // Cache-elt voucher lekérés
  const restaurantIds = useMemo(() => restaurants.filter(r => !effectiveCity || r.city === effectiveCity).map(r => r.id), [restaurants, effectiveCity]);
  const { menusByRestaurant, loading: menusLoading } = useAllDailyMenus(selectedDate, restaurantIds);
  const secondaryReady = useDeferredWork(!authLoading && !restaurantsLoading && !citiesLoading && !menusLoading && restaurantIds.length > 0);
  const { data: restaurantVouchers = {} } = useRestaurantVouchers(restaurantIds, secondaryReady);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    // Check if app is installed (standalone mode)
    const checkInstalled = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
      setIsInstalled(standalone);
    };
    checkInstalled();
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);
  useEffect(() => {
    if (jumpToRestaurant) {
      const element = document.getElementById(jumpToRestaurant);
      if (element) {
        // Show header before scrolling
        setHeaderVisible(true);
        element.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
        setJumpToRestaurant("");
      }
    }
  }, [jumpToRestaurant]);
  const handleLogout = async () => {
    try {
      await supabase.auth.signOut({ scope: "local" });
    } catch {
      // ignore
    }
    toast.success("Sikeres kijelentkezés");
    navigate("/");
  };
  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // Asztali böngészőkben (Edge, Chrome) manuális telepítés útmutató
      toast.info("Telepítés: Kattints a címsor mellett lévő + vagy telepítés ikonra, vagy használd a böngésző menüjét (⋮) → 'Alkalmazás telepítése'");
      return;
    }
    deferredPrompt.prompt();
    const {
      outcome
    } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      toast.success("Az alkalmazás telepítése megkezdődött!");
    }
    setDeferredPrompt(null);
  };



  // Fetch city ID for banner/popup filtering
  const [selectedCityId, setSelectedCityId] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchCityId = async () => {
      if (!secondaryReady || !effectiveCity) {
        setSelectedCityId(null);
        return;
      }
      const { data } = await supabase
        .from("cities")
        .select("id")
        .eq("name", effectiveCity)
        .single();
      setSelectedCityId(data?.id || null);
    };
    fetchCityId();
  }, [effectiveCity, secondaryReady]);

  // Fetch all menus for all restaurants in ONE batch query
  // Fetch active banners
  const { data: bannersByPosition } = useBanners(selectedCityId, secondaryReady && Boolean(selectedCityId));
  const sortedAndFilteredRestaurants = useMemo(() => {
    let filtered = restaurants.filter(r => {
      const matchesSearch = r.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCity = !effectiveCity || r.city === effectiveCity;
      return matchesSearch && matchesCity;
    });

    // Distance ignores all featured/favorite priorities; missing coordinates go last.
    if (sortBy === "distance") {
      return coordinates ? sortRestaurantsByDistance(filtered, coordinates) : filtered;
    }

    // Separate into PRO, favorites, and regular restaurants
    const proRestaurants = filtered.filter(r => r.is_pro);
    const favoriteRestaurants = filtered.filter(r => !r.is_pro && favorites.includes(r.id));
    const regularRestaurants = filtered.filter(r => !r.is_pro && !favorites.includes(r.id));

    // Sort PRO restaurants by priority (higher priority first)
    const sortedPro = proRestaurants.sort((a, b) => {
      // Higher priority number should come first (descending order)
      const priorityDiff = (b.highlight_priority || 0) - (a.highlight_priority || 0);
      if (priorityDiff !== 0) return priorityDiff;

      // For equal priorities, randomize order
      return Math.random() - 0.5;
    });

    // Sort favorite restaurants
    const sortedFavorites = favoriteRestaurants.sort((a, b) => {
      if (sortBy === "name") {
        return a.name.localeCompare(b.name, "hu");
      } else if (sortBy === "price") {
        return (a.dine_in_price || 0) - (b.dine_in_price || 0);
      } else {
        return Math.random() - 0.5;
      }
    });

    // Sort regular restaurants
    const sortedRegular = regularRestaurants.sort((a, b) => {
      // Higher priority number should come first (descending order)
      const priorityDiff = (b.highlight_priority || 0) - (a.highlight_priority || 0);
      if (priorityDiff !== 0) return priorityDiff;

      // For equal priorities, randomize order
      return Math.random() - 0.5;
    });

    // PRO first, then favorites, then regular
    return [...sortedPro, ...sortedFavorites, ...sortedRegular];
  }, [restaurants, searchTerm, sortBy, effectiveCity, favorites, coordinates]);
  const handleWelcomeComplete = (name: string, city: string) => {
    setUserName(name);
    setSelectedCity(city);
    localStorage.setItem("napihami_welcomed", "true");
    localStorage.setItem("napihami_username", name);
    localStorage.setItem("napihami_city", city);
    setWelcomeDialogOpen(false);
    
    // Navigate to city URL
    navigate(`/${cityNameToSlug(city)}`, { replace: true });
    
    // Start tour after welcome dialog closes if not completed before
    const tourCompleted = localStorage.getItem("napihami_tour_completed");
    if (!tourCompleted) {
      // Small delay to let the page render
      setTimeout(() => setRunTour(true), 500);
    }
  };
  
  const handleRestartTour = () => {
    localStorage.removeItem("napihami_tour_completed");
    setUserProfileDialogOpen(false);
    // Small delay to let dialog close
    setTimeout(() => setRunTour(true), 300);
  };
  
  const handleTourFinish = () => {
    setRunTour(false);
  };
  const handleCityChange = (city: string) => {
    setSelectedCity(city);
    localStorage.setItem("napihami_city", city);
    navigate(`/${cityNameToSlug(city)}`, { replace: true });
  };
  const handleSortChange = (sort: SortOption) => {
    setSortBy(sort);
    localStorage.setItem("napihami_sortby", sort);
    document.cookie = `napihami_sortby=${sort}; Max-Age=31536000; Path=/; SameSite=Lax${window.location.protocol === "https:" ? "; Secure" : ""}`;
    if (sort === "distance") requestLocation();
    else cancelLocation();
  };
  const handleViewModeChange = (value: ViewMode) => {
    setViewMode(value);
    localStorage.setItem("napihami_viewmode", value);
  };
  const handleFontSizeChange = (value: FontSizeOption) => {
    setFontSizeOffset(value);
    localStorage.setItem("napihami_fontsize", value);
  };
  const handleToggleFavorite = (restaurantId: string) => {
    setFavorites(prev => {
      const newFavorites = prev.includes(restaurantId) ? prev.filter(id => id !== restaurantId) : [...prev, restaurantId];
      localStorage.setItem("napihami_favorites", JSON.stringify(newFavorites));
      return newFavorites;
    });
  };

  // Hide header after scrolling down 200px - throttled with requestAnimationFrame
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          setHeaderVisible(scrollY <= 200);
          setShowScrollTop(scrollY > 300);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };
  
  const [previewToken] = useState(getStoredPreviewToken);
  const { data: previewData } = useRestaurantPreview(previewToken, selectedDate);
  const preview = previewData?.status === "ok" ? previewData : null;
  const displayRestaurants = preview
    ? [preview.restaurant, ...sortedAndFilteredRestaurants.filter(r => r.id !== preview.restaurant.id)]
    : sortedAndFilteredRestaurants;
  const getMenus = (id: string) => preview && id === preview.restaurant.id ? preview.menus : menusByRestaurant.get(id);

  const loading = authLoading || restaurantsLoading || citiesLoading || Boolean(citySlug && !routeCity);
  
  if (loading) {
    return <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner />
      </div>;
  }
  return <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <SEO
        title={selectedCity ? `${selectedCity} napi menü ajánlatok - NapiHami.hu` : "NapiHami.hu - Napi menü ajánlatok - AZ Ebédkeresőd!"}
        description={selectedCity ? `Friss napi menü ajánlatok ${selectedCity} éttermeiből. Találd meg a legjobb ebédet a környékeden!` : "Napi menü ajánlatok Magyarországon. Találd meg a legjobb ebédet a környékeden!"}
        path={selectedCity ? `/${cityNameToSlug(selectedCity)}` : "/"}
      />
      <WelcomeDialog cities={cities} onComplete={handleWelcomeComplete} open={welcomeDialogOpen && !preview} onOpenChange={setWelcomeDialogOpen} />
      {profileMounted && <Suspense fallback={userProfileDialogOpen ? <LoadingSpinner /> : null}><UserProfileDialog 
        open={userProfileDialogOpen} 
        onOpenChange={setUserProfileDialogOpen} 
        user={authUser} 
        role={role} 
        cities={cities} 
        userName={userName}
        onRestartTour={handleRestartTour}
      /></Suspense>}
      
      {runTour && <Suspense fallback={null}><OnboardingTour run={runTour} onFinish={handleTourFinish} /></Suspense>}

      {secondaryReady && authUser && <Suspense fallback={null}><FavoriteFoodAlert selectedDate={selectedDate} onRestaurantClick={setJumpToRestaurant} /></Suspense>}
      
      <header className={`border-b fixed top-0 left-0 right-0 z-50 shadow-lg transition-transform duration-300 ${headerVisible ? "translate-y-0" : "-translate-y-full"}`} style={{ backgroundColor: 'rgba(69, 113, 243, 0.8)' }}>
        <div className="container mx-auto px-4 py-4 bg-transparent">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4 tour-logo-section">
              <div className="relative">
                <img src={logo} alt="NapiHami" className="h-12 md:h-16 w-auto" width={168} height={64} fetchPriority="high" decoding="async" />
                <span className="absolute -bottom-1 -right-1 text-[9px] md:text-[10px] font-semibold text-white/80 bg-black/30 px-1.5 py-0.5 rounded-full tracking-tight">
                  v{APP_VERSION}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div>
                  <h1 className="text-xl md:text-2xl font-bold text-white drop-shadow-md my-0 py-0">AZ Ebédkeresőd</h1>
                  {userName && <p className="text-sm text-white/90">Üdv, {userName}!</p>}
                </div>
                {safeFacebookUrl && (
                  <a 
                    id="link-facebook"
                    href={safeFacebookUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    aria-label="Facebook oldal megnyitása" 
                    className="inline-flex items-center justify-center transition-opacity hover:opacity-80"
                  >
                    <img 
                      src={facebookIcon} 
                      alt="Facebook" 
                      className="w-[38px] h-[38px] object-contain"
                    />
                  </a>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
                <CartButton />
                {!isInstalled && (
                  <MobileTooltip content="Telepítsd az alkalmazást a készülékedre a gyorsabb eléréshez">
                    <Button id="btn-install-app" variant="outline" size="default" onClick={handleInstallClick} className="bg-white/90 hover:bg-white flex items-center gap-2 tour-install-btn">
                      <Download className="h-4 w-4" />
                      Telepítés
                    </Button>
                  </MobileTooltip>
                )}
                <MobileTooltip content="Profilom megtekintése és szerkesztése">
                  <Button id="btn-user-profile" variant="outline" size="icon" onClick={() => setUserProfileDialogOpen(true)} className="bg-white/90 hover:bg-white tour-profile-btn">
                    <User className="h-4 w-4" />
                  </Button>
                </MobileTooltip>
                {!session && (
                  <MobileTooltip content="Iratkozz fel a napi ebédértesítőre, hogy ne maradj le a legjobb ajánlatokról">
                    <span>
                      <DeferredNewsletterDialog cities={cities} userName={userName} />
                    </span>
                  </MobileTooltip>
                )}
                {session && role !== "guest" && (
                  <MobileTooltip content="Adminisztrációs felület megnyitása">
                    <Button id="btn-admin-panel" variant="outline" size="icon" onClick={() => navigate("/admin")} className="bg-white/90 hover:bg-white">
                      <Settings className="h-4 w-4" />
                    </Button>
                  </MobileTooltip>
                )}
                {session && (
                  <MobileTooltip content="Kijelentkezés a rendszerből">
                    <Button id="btn-logout" variant="outline" size="icon" onClick={handleLogout} className="bg-white/90 hover:bg-white">
                      <LogOut className="h-4 w-4" />
                    </Button>
                  </MobileTooltip>
                )}
                {!session && (
                  <MobileTooltip content="Bejelentkezés a rendszerbe">
                    <Button id="btn-login" variant="outline" size="icon" onClick={() => navigate("/login")} className="bg-white/90 hover:bg-white">
                      <LogIn className="h-4 w-4" />
                    </Button>
                  </MobileTooltip>
                )}
              </div>
          </div>
        </div>
      </header>

      <div className="w-full py-8 mt-[150px] md:mt-[96px] bg-no-repeat bg-bottom" style={{
      backgroundImage: `url(/bg_page_head.webp)`,
      backgroundSize: "100% auto"
    }}>
        <div className="container mx-auto px-4">
          <div className="bg-dateBanner text-dateBanner-foreground px-6 py-3 rounded-[7px] flex items-center justify-center gap-3 font-semibold text-lg tour-date-nav">
            {canGoPrevious && <button id="btn-date-prev" onClick={goToPreviousDay} className="hover:opacity-70 transition-opacity flex items-center gap-1 text-sm md:text-base" aria-label="Előző nap">
                ◄ <span className="hidden sm:inline">(előző nap)</span>
              </button>}
            <span className="text-center">
              {isToday ? `MAI KÍNÁLAT - ${format(selectedDate, "yyyy. MMMM d. (EEEE)", {
              locale: hu
            })}` : format(selectedDate, "yyyy. MMMM d. (EEEE)", {
              locale: hu
            }).toUpperCase()}
            </span>
            {canGoNext && <button id="btn-date-next" onClick={goToNextDay} className="hover:opacity-70 transition-opacity flex items-center gap-1 text-sm md:text-base" aria-label="Következő nap">
                <span className="hidden sm:inline">(következő nap)</span> ►
              </button>}
          </div>
        </div>
      </div>

      <main className="container mx-auto px-4 py-8 pb-[60px]">
        <div className="mb-6 space-y-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input type="text" placeholder="Étterem keresése..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" />
            </div>
            <div className="flex items-center gap-2 w-full md:w-auto tour-city-select">
              <img src={citySilhouette} alt="Város" className="h-8" style={{ width: '115px', height: '32px' }} width={115} height={32} loading="lazy" decoding="async" />
              <Select value={selectedCity} onValueChange={handleCityChange}>
                <SelectTrigger className="w-full md:w-[200px]">
                  <SelectValue placeholder="Válassz várost" />
                </SelectTrigger>
                <SelectContent>
                  {cities.map(city => <SelectItem key={city} value={city}>
                      {city}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:flex-wrap">
            <div className="flex gap-2 items-center tour-sort-select">
              <span className="text-sm font-medium">Rendezés:</span>
              <Select value={sortBy} onValueChange={value => handleSortChange(value as SortOption)}>
                <SelectTrigger className="w-[180px]" aria-label="Rendezés">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="random">Véletlenszerű</SelectItem>
                  <SelectItem value="name">ABC</SelectItem>
                  <SelectItem value="price">Ár szerint</SelectItem>
                  <SelectItem value="distance">Távolság szerint</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex gap-2 items-center">
              <span className="text-sm font-medium">Ugrás:</span>
              <Select value={jumpToRestaurant} onValueChange={setJumpToRestaurant}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Válassz éttermet" />
                </SelectTrigger>
                <SelectContent>
                  {sortedAndFilteredRestaurants.map(restaurant => <SelectItem key={restaurant.id} value={restaurant.id}>
                      {restaurant.name}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            
            <div className="hidden md:flex gap-2 items-center tour-view-select">
              <span className="text-sm font-medium">Nézet:</span>
              <Select value={viewMode} onValueChange={value => handleViewModeChange(value as ViewMode)}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 / sor</SelectItem>
                  <SelectItem value="2">2 / sor</SelectItem>
                  <SelectItem value="3">3 / sor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex gap-2 items-center">
              <span className="text-sm font-medium">Betűméret:</span>
              <Select value={fontSizeOffset} onValueChange={value => handleFontSizeChange(value as FontSizeOption)}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Normál</SelectItem>
                  <SelectItem value="1">+1px</SelectItem>
                  <SelectItem value="2">+2px</SelectItem>
                  <SelectItem value="3">+3px</SelectItem>
                  <SelectItem value="4">+4px</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {sortBy === "distance" && <div className="mb-4 flex flex-wrap items-center gap-2 text-sm" role="status" aria-live="polite">
          {locationLoading ? <span className="text-muted-foreground">Helymeghatározás folyamatban…</span>
            : locationError ? <span className="text-destructive">{locationError}</span>
            : !coordinates ? <span className="text-muted-foreground">A távolság szerinti rendezéshez add meg a jelenlegi helyed.</span> : null}
          {!locationLoading && <Button variant="outline" size="sm" onClick={requestLocation}>
            {locationError ? "Újrapróbálás" : coordinates ? "Helyzet frissítése" : "Helymeghatározás"}
          </Button>}
        </div>}

        {preview && <PreviewBar restaurantName={preview.restaurant.name} expiresAt={preview.expires_at} />}

        <div className="tour-map">
          <RestaurantMap 
            restaurants={sortedAndFilteredRestaurants} 
            selectedCity={selectedCity}
            onJumpToRestaurant={setJumpToRestaurant}
          />
        </div>

        <div 
          className={`grid gap-6 ${viewMode === "1" ? "md:grid-cols-1 md:max-w-[900px] md:mx-auto" : viewMode === "3" ? "md:grid-cols-3" : "md:grid-cols-2"}`}
          style={{ '--font-size-offset': `${fontSizeOffset}px` } as React.CSSProperties}
        >
          {displayRestaurants.map((restaurant, index) => {
            // Determine if a banner should appear after this restaurant
            // banner1: after 1st (index 0), banner2: after 4th (index 3), banner3: after 7th (index 6)
            const bannerPosition = 
              index === 0 ? "banner1" : 
              index === 3 ? "banner2" : 
              index === 6 ? "banner3" : null;
            
            const banner = bannerPosition && bannersByPosition ? bannersByPosition[bannerPosition] : null;
            
            return (
              <div key={restaurant.id} ref={index === 0 ? firstRestaurantRef : null} className="contents">
                <RestaurantCard 
                  restaurant={restaurant} 
                  isFavorite={favorites.includes(restaurant.id)} 
                  onToggleFavorite={handleToggleFavorite} 
                  menusWithItems={getMenus(restaurant.id)}
                  menusLoading={preview?.restaurant.id === restaurant.id ? false : menusLoading}
                  menuDate={selectedDate}
                  vouchers={restaurantVouchers[restaurant.id]}
                />
                {banner && (
                  <div className={isMobile || viewMode === "1" ? "col-span-full" : ""}>
                    <BannerAd banner={banner} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {displayRestaurants.length === 0 && <div className="text-center py-12">
            <p className="text-muted-foreground">Nem található étterem</p>
          </div>}
      </main>

      {/* Scroll to top button */}
      <button id="btn-scroll-top" onClick={scrollToTop} className={`fixed bottom-6 right-6 z-40 p-3 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-all duration-300 ${showScrollTop ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10 pointer-events-none"}`} aria-label="Vissza a tetejére">
        <ArrowUp className="h-5 w-5" />
      </button>

      <FloatingContactButton />
      <FloatingRecommendBar key={effectiveCity || "none"} defaultCity={effectiveCity || undefined} />
      {secondaryReady && <Suspense fallback={null}><Footer /></Suspense>}
      {secondaryReady && selectedCityId && <Suspense fallback={null}><PopupDisplay currentCityId={selectedCityId} /></Suspense>}
    </div>;
};
export default Index;